import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllUserRole } from '../../../../controllers/UserRoleController';
import { createUser } from '../../../../controllers/UserController';
import ButtonPrimary from '../../../../components/button/ButtonPrimary';
import ButtonSecondary from '../../../../components/button/ButtonSecondary';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { toast } from 'react-hot-toast';
import { useTranslation } from 'react-i18next';

const CreateModal = () => {
    const { t } = useTranslation();
    const [name, setName] = useState('');
    const [gender, setGender] = useState('');
    const [username, setUsername] = useState('');
    const [email, setEmail] = useState('');
    const [roles, setRoles] = useState([]);
    const [roleID, setRoleID] = useState('');
    const [password, setPassword] = useState('');
    const [showPassword, setShowPassword] = useState(false);
    const navigate = useNavigate();
    
    useEffect(() => {
        const fetchRoles = async () => {
            try {
                const result = await getAllUserRole();
                const formattedRoles = result.roles.map(role => ({
                    id: role.id,
                    name: t(`common.role.${role.name.toLowerCase().replace(/\s(.)/g, (match) => match.toUpperCase()).replace(/\s/g, '')}`, { defaultValue: role.name })
                }));
                
                setRoles(formattedRoles);
            } catch (error) {
                toast.error(t('userRole.fetchData.errorMessage'));
            };
        };
        fetchRoles();
    }, [t]);
    
    const toggleShowPassword = () => {
        setShowPassword((prev) => !prev);
    };
    
    const isDisabled = useMemo(() => {
        return !(name.trim() && gender.trim() && username.trim() && email.trim() && roleID && password.length >= 8);
    }, [name, gender, username, email, roleID, password]);
    
    const handleCreate = async () => {
        try {
            await createUser({ name, gender, username, email, role_id: roleID, password });
            navigate('/user');
            toast.success(t('user.add.message'));
        } catch (error) {
            toast.error(error.message);
        };
    };
    
    const handleBack = () => {
        navigate('/user');
    };
    
    return (
        <div className='flex flex-col p-5 rounded-lg bg-white space-y-5'>
            <div className='flex flex-grow items-center'>
                <span className='w-36 text-xs md:text-sm'>{t('common.name.title')}:</span>
                <input
                    className={`
                        w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                        hover:outline-none hover:border-grayX11
                        focus:outline-none focus:border-blackOlive
                    `}
                    type='text'
                    placeholder={t('common.name.placeholderText')}
                    value={name}
                    onChange={(e) => setName(e.target.value)} 
                />
            </div>
            <div className='flex flex-grow items-center'>
                <span className='w-36 text-xs md:text-sm'>{t('common.gender.title')}:</span>
                <select
                    className={`
                        w-full px-3 py-2 rounded bg-white border border-platinum text-xs md:text-sm
                        hover:outline-none hover:border-grayX11
                        focus:outline-none focus:border-blackOlive
                    `}
                    value={gender}
                    onChange={(e) => setGender(e.target.value)}
                >
                    <option value='' disabled={gender !== ''}>{t('common.gender.placeholderSelect')}</option>
                    <option value='Male'>{t('common.gender.male')}</option>
                    <option value='Female'>{t('common.gender.female')}</option>
                </select>
            </div>
            <div className='space-y-5'>
                <span className='text-base md:text-lg font-medium'>{t('common.account')}:</span>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 text-xs md:text-sm'>{t('common.role.title')}:</span>
                    <select
                        className={`
                            w-full px-3 py-2 rounded bg-white border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        value={roleID}
                        onChange={(e) => setRoleID(e.target.value)}
                        disabled={roles.length === 0}
                    >
                        <option value='' disabled={roleID !== ''}>{t('common.role.placeholderSelect')}</option>
                        {roles.map((role) => (
                            <option key={role.id} value={role.id}>{role.name}</option>
                        ))}
                    </select>
                </div>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 text-xs md:text-sm'>{t('common.username.title')}:</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        placeholder={t('common.username.placeholderText')}
                        value={username}
                        onChange={(e) => {
                            const input = e.target.value.toLowerCase();
                            setUsername(input)
                        }}
                    />
                </div>
                <div className='flex flex-grow items-center'>
                    <span className='w-36 text-xs md:text-sm'>{t('common.email.title')}:</span>
                    <input
                        className={`
                            w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                            hover:outline-none hover:border-grayX11
                            focus:outline-none focus:border-blackOlive
                        `}
                        type='text'
                        placeholder={t('common.email.placeholderText')}
                        value={email}
                        onChange={(e) => {
                            const input = e.target.value.toLowerCase();
                            setEmail(input)
                        }} 
                    />
                </div>
                <div className='relative'>
                    <div className='flex flex-grow items-center'>
                        <label className='w-36 text-xs md:text-sm'>{t('common.password.title')}:</label>
                        <input
                            className={`
                                w-full px-3 py-2 rounded border border-platinum text-xs md:text-sm
                                hover:outline-none hover:border-grayX11
                                focus:outline-none focus:border-blackOlive
                            `}
                            type={showPassword ? 'text' : 'password'}
                            placeholder={t('common.password.placeholderText')}
                            required
                            value={password}
                            onChange={(e) => {
                                const input = e.target.value.replace(/\s/g, '');
                                setPassword(input);
                            }} 
                        />
                        <button
                            className='absolute flex right-3 pl-3 bg-white text-grayX11'
                            type='button'
                            aria-label={showPassword ? t('common.password.ariaLabelHide') : t('common.password.ariaLabelShow')}
                            onClick={toggleShowPassword}
                        >
                            {showPassword ? <VisibilityOffIcon sx={{ fontSize: '1.25rem' }} /> : <VisibilityIcon sx={{ fontSize: '1.25rem' }} />}
                        </button>
                    </div>
                </div>
            </div>
            <div className='flex justify-center sm:justify-end space-x-5'>
                <ButtonSecondary className='h-full w-24 md:w-32 md:text-sm' onClick={handleBack}>{t('common.action.back')}</ButtonSecondary>
                <ButtonPrimary className='h-full w-24 md:w-32 md:text-sm' isDisabled={isDisabled} onClick={handleCreate}>{t('common.action.add')}</ButtonPrimary>
            </div>
        </div>
    );
};

export default CreateModal;
